import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import {
  ArrowRight,
  BarChart3,
  Check,
  ChevronRight,
  CircleCheck,
  Globe2,
  Mail,
  MousePointer2,
  Palette,
  Sparkles,
  X,
  Zap,
} from 'lucide-react'
import { api } from '../lib/api'
import { appUrl, PUBLIC_ORIGIN } from '../lib/config'
import { PagePreview } from './ui/PagePreview'
import { PhoneFrame } from './ui/PhoneFrame'
import { BrandLogo } from './ui/BrandLogo'
import { SAMPLE_PAGE } from '../lib/samplePage'

const PUBLIC_HOST = PUBLIC_ORIGIN.replace(/^https?:\/\//, '')

const MAKERS = [
  ['DC', 'Damon', '6 launches'],
  ['AK', 'Arvid', '3 products'],
  ['MR', 'Marie', 'Bootstrapped'],
  ['SW', 'Sam', 'Building now'],
  ['JL', 'Justin', '12 launches'],
  ['KP', 'Katelyn', 'Indie maker'],
  ['RM', 'Ruben', 'Growing'],
]

const FEATURES = [
  'Every startup, launch, and milestone',
  'Email capture and audience growth',
  'Privacy-friendly visitor analytics',
  'Custom domain and search-ready pages',
  '30 themes and 9 carefully chosen fonts',
]

const HERO_THEMES = [
  { theme: 'sunset', font: 'fraunces', name: 'Sunset', fontName: 'Fraunces' },
  { theme: 'aqua', font: 'dmMono', name: 'Aqua', fontName: 'DM Mono' },
  { theme: 'plum', font: 'playfair', name: 'Plum', fontName: 'Playfair' },
  { theme: 'lime', font: 'spaceGrotesk', name: 'Lime', fontName: 'Space Grotesk' },
  { theme: 'rose', font: 'sora', name: 'Rose', fontName: 'Sora' },
  { theme: 'midnight', font: 'geist', name: 'Midnight', fontName: 'Geist' },
]

const FAQS = [
  {
    question: 'Is MakerStack another link-in-bio tool?',
    answer:
      'It goes further. MakerStack is built around the story of what you make: your products, launches, proof, and momentum—all on one page you own.',
  },
  {
    question: 'Can I edit my page before paying?',
    answer:
      'Yes. Claim your username, build your page, and preview everything for free. You only pay when you are ready to publish.',
  },
  {
    question: 'Is there a monthly subscription?',
    answer:
      'No. Choose a one-year pass for $25 or lifetime access for $45. Both are one-time payments.',
  },
  {
    question: 'Can I use my own domain?',
    answer:
      'Yes. Connect a custom domain and make your MakerStack page feel completely yours.',
  },
]

function useDebounced<T>(value: T, ms: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), ms)
    return () => clearTimeout(id)
  }, [value, ms])
  return debouncedValue
}

function UsernameClaim({ compact = false }: { compact?: boolean }) {
  const [username, setUsername] = useState('')
  const normalized = username.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '')
  const debounced = useDebounced(normalized, 350)

  const availability = useQuery(
    api.pages.checkUsernameAvailable,
    debounced.length >= 3 ? { username: debounced } : 'skip',
  )
  const canClaim = debounced.length >= 3 && availability?.available === true
  const isChecking = debounced.length >= 3 && availability === undefined

  function claim() {
    if (!canClaim) return
    window.location.href = appUrl(`/login?claim=${encodeURIComponent(debounced)}`)
  }

  const statusIcon = useMemo(() => {
    if (debounced.length < 3 || !availability) return null
    return availability.available ? <Check aria-hidden="true" /> : <X aria-hidden="true" />
  }, [availability, debounced])

  return (
    <div className={`claim-wrap ${compact ? 'claim-wrap--compact' : ''}`}>
      <div className="claim-form">
        <label className="claim-input">
          <span>{PUBLIC_HOST}/</span>
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && claim()}
            placeholder="yourname"
            aria-label="Choose your MakerStack username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <span className={canClaim ? 'claim-status is-available' : 'claim-status'}>
            {statusIcon}
          </span>
        </label>
        <button onClick={claim} disabled={!canClaim} className="primary-button">
          Claim my page <ArrowRight aria-hidden="true" />
        </button>
      </div>
      <div className="claim-note" aria-live="polite">
        {isChecking ? (
          <span className="is-checking"><i aria-hidden="true" /> Checking availability…</span>
        ) : debounced.length >= 3 && availability && !availability.available ? (
          <span className="is-taken">{availability.reason}</span>
        ) : canClaim ? (
          <span className="is-available">
            <CircleCheck aria-hidden="true" /> {PUBLIC_HOST}/{debounced} is yours
          </span>
        ) : (
          <span>No credit card. Build and preview for free.</span>
        )}
      </div>
    </div>
  )
}

function ThemePhonePreview() {
  const [themeIndex, setThemeIndex] = useState(0)
  const activeTheme = HERO_THEMES[themeIndex]

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) return
    const interval = window.setInterval(
      () => setThemeIndex((current) => (current + 1) % HERO_THEMES.length),
      3200,
    )
    return () => window.clearInterval(interval)
  }, [])

  const previewPage = {
    ...SAMPLE_PAGE,
    theme: activeTheme.theme,
    font: activeTheme.font,
  }

  return (
    <div className={`theme-stage theme-stage--${activeTheme.theme}`}>
      <div className="theme-stage__header">
        <span><Sparkles aria-hidden="true" /> Live style preview</span>
        <strong>{activeTheme.name} · {activeTheme.fontName}</strong>
      </div>
      <div className="theme-device" key={`${activeTheme.theme}-${activeTheme.font}`}>
        <PhoneFrame>
          <PagePreview page={previewPage} phone />
        </PhoneFrame>
      </div>
      <div className="theme-switcher" aria-label="Choose a preview theme">
        {HERO_THEMES.map((theme, index) => (
          <button
            key={theme.theme}
            className={`theme-swatch theme-swatch--${theme.theme}${index === themeIndex ? ' is-active' : ''}`}
            onClick={() => setThemeIndex(index)}
            aria-label={`Preview ${theme.name} theme with ${theme.fontName}`}
            aria-pressed={index === themeIndex}
          >
            <span />
          </button>
        ))}
      </div>
    </div>
  )
}

export function LandingPage() {
  return (
    <div className="landing-shell">
      <header className="site-header">
        <Link to="/" className="brand-link">
          <BrandLogo />
        </Link>
        <nav className="site-nav" aria-label="Main navigation">
          <a href="#why">Why MakerStack</a>
          <a href="#features">Features</a>
          <Link to="/pricing">Pricing</Link>
        </nav>
        <div className="nav-actions">
          <a href={appUrl('/login')} className="login-link">
            Log in
          </a>
          <a href="#claim" className="nav-cta">
            Build your page <ArrowRight aria-hidden="true" />
          </a>
        </div>
      </header>

      <main>
        <section className="hero-section" id="claim">
          <div className="hero-orb hero-orb--one" />
          <div className="hero-orb hero-orb--two" />
          <div className="hero-copy">
            <div className="eyebrow">
              <span className="eyebrow-dot" /> The home base for serious makers
            </div>
            <h1>
              Your work deserves more than a <em>link in bio.</em>
            </h1>
            <p className="hero-lede">
              Turn every product, launch, and lesson into a story people remember—and a page that
              keeps working long after you post.
            </p>
            <UsernameClaim />
            <div className="hero-proof">
              <div className="proof-avatars" aria-hidden="true">
                {MAKERS.slice(0, 5).map(([initials]) => (
                  <span key={initials}>{initials}</span>
                ))}
              </div>
              <div>
                <div className="proof-stars">★★★★★</div>
                <p>Made for builders who ship in public</p>
              </div>
            </div>
          </div>

          <div className="hero-visual" aria-label="Preview of a MakerStack page">
            <div className="floating-note floating-note--top">
              <span className="note-icon"><Zap aria-hidden="true" /></span>
              <div><strong>New launch added</strong><small>Momentum, captured.</small></div>
            </div>
            <ThemePhonePreview />
            <div className="floating-note floating-note--bottom">
              <span className="metric">+28%</span>
              <div><strong>Audience growth</strong><small>Last 30 days</small></div>
            </div>
            <div className="cursor-chip"><MousePointer2 aria-hidden="true" /> Live page</div>
          </div>
        </section>

        <section className="maker-strip" aria-label="Made for independent builders">
          <p>Built for the people building what’s next</p>
          <div className="maker-track">
            {MAKERS.map(([initials, name, detail]) => (
              <div className="maker-pill" key={name}>
                <span>{initials}</span>
                <div><strong>{name}</strong><small>{detail}</small></div>
              </div>
            ))}
          </div>
        </section>

        <section className="manifesto-section" id="why">
          <span className="section-kicker">Your unfair advantage</span>
          <h2>The internet sees the launch.<br />MakerStack tells the <em>whole story.</em></h2>
          <p>
            A feed forgets. Your MakerStack compounds—turning scattered updates into proof that
            you know how to build, learn, and grow.
          </p>
          <div className="manifesto-rule"><span>01</span><i /><span>05</span></div>
        </section>

        <section className="feature-story" id="features">
          <article className="story-row">
            <div className="story-copy">
              <span className="section-kicker">01 · Your story</span>
              <h2>Make the journey<br />part of the product.</h2>
              <p>
                Show the bets, breakthroughs, pivots, and lessons behind your work. Give people a
                reason to care about what you’re building next.
              </p>
              <a href="#claim" className="text-link">Start your story <ChevronRight aria-hidden="true" /></a>
            </div>
            <div className="story-visual timeline-card">
              <div className="card-topline"><span>BUILD LOG</span><span>2026</span></div>
              <div className="timeline-list">
                <div><time>JAN 08</time><span className="timeline-dot" /><p><strong>First idea, first commit</strong><small>Started with a problem I couldn’t ignore.</small></p></div>
                <div><time>FEB 21</time><span className="timeline-dot is-hot" /><p><strong>100 people joined the waitlist</strong><small>Proof that the problem was real.</small></p></div>
                <div><time>APR 14</time><span className="timeline-dot" /><p><strong>Launched v1 into the world</strong><small>Rough edges. Real customers.</small></p></div>
              </div>
              <div className="timeline-stamp"><Sparkles aria-hidden="true" /> Story worth following</div>
            </div>
          </article>

          <article className="story-row story-row--reverse">
            <div className="story-copy">
              <span className="section-kicker">02 · Your signal</span>
              <h2>Know what earns<br />people’s attention.</h2>
              <p>
                See what visitors explore, where your audience comes from, and which launches pull
                people closer—without drowning in dashboards.
              </p>
              <a href="#claim" className="text-link">Grow with clarity <ChevronRight aria-hidden="true" /></a>
            </div>
            <div className="story-visual analytics-card">
              <div className="analytics-heading"><div><small>PAGE VIEWS</small><strong>12,840</strong></div><span>+28.4%</span></div>
              <div className="chart-bars" aria-hidden="true">
                {Array.from({ length: 12 }).map((_, index) => <i key={index} />)}
              </div>
              <div className="chart-axis"><span>Jan</span><span>Mar</span><span>May</span><span>Jul</span></div>
              <div className="analytics-footer"><span><i className="legend-primary" /> Returning</span><span><i /> New visitors</span></div>
            </div>
          </article>
        </section>

        <section className="bento-section">
          <div className="section-heading">
            <span className="section-kicker">Everything you need</span>
            <h2>One page. Every reason<br />to <em>remember you.</em></h2>
          </div>
          <div className="bento-grid">
            <article className="bento-card bento-card--audience">
              <div className="bento-icon"><Mail aria-hidden="true" /></div>
              <h3>Build an audience you own</h3>
              <p>Collect emails directly from your page and turn passing interest into a lasting connection.</p>
              <div className="subscriber-stack">
                <div><span>JM</span><p><strong>Jamie M.</strong><small>joined your audience</small></p><time>now</time></div>
                <div><span>AK</span><p><strong>Alex K.</strong><small>joined your audience</small></p><time>2m</time></div>
                <div><span>RP</span><p><strong>Robin P.</strong><small>joined your audience</small></p><time>8m</time></div>
              </div>
            </article>
            <article className="bento-card bento-card--domain">
              <div className="bento-icon"><Globe2 aria-hidden="true" /></div>
              <h3>Make it unmistakably yours</h3>
              <p>Bring your own domain and show up with a page that feels like your brand—not ours.</p>
              <div className="domain-field"><span className="domain-lock">●</span><strong>you.com</strong><CircleCheck aria-hidden="true" /></div>
            </article>
            <article className="bento-card bento-card--style">
              <div className="bento-icon"><Palette aria-hidden="true" /></div>
              <h3>Polished without the pixel pushing</h3>
              <p>Choose from thoughtful themes and fonts. Every combination is responsive, fast, and ready to share.</p>
              <div className="theme-deck" aria-hidden="true"><i /><i /><i /><i /></div>
            </article>
            <article className="bento-card bento-card--speed">
              <div className="bento-icon"><BarChart3 aria-hidden="true" /></div>
              <h3>Fast by default. Search-ready.</h3>
              <p>Pages are built to load quickly, look sharp everywhere, and help people discover your work.</p>
              <div className="speed-score"><strong>98</strong><span>Performance</span><i /></div>
            </article>
          </div>
        </section>

        <section className="pricing-section">
          <div className="pricing-copy">
            <span className="section-kicker">Simple on purpose</span>
            <h2>Pay once.<br /><em>Keep building.</em></h2>
            <p>No monthly tax on your ambition. Build and preview for free, then choose the pass that fits your path.</p>
            <ul>
              {FEATURES.map((feature) => <li key={feature}><Check aria-hidden="true" /> {feature}</li>)}
            </ul>
          </div>
          <div className="pricing-cards">
            <article className="price-card">
              <span className="price-label">1-Year Pass</span>
              <div className="price"><strong>$25</strong><span>USD</span></div>
              <p>For your next chapter.</p>
              <Link to="/pricing" className="secondary-button">Choose 1 year <ArrowRight aria-hidden="true" /></Link>
            </article>
            <article className="price-card price-card--featured">
              <span className="popular-badge">BEST VALUE</span>
              <span className="price-label">Lifetime</span>
              <div className="price"><del>$75</del><strong>$45</strong><span>USD</span></div>
              <p>One payment. Yours for good.</p>
              <Link to="/pricing" className="primary-button">Get lifetime <ArrowRight aria-hidden="true" /></Link>
            </article>
          </div>
        </section>

        <section className="faq-section">
          <div className="faq-heading"><span className="section-kicker">Good questions</span><h2>Before you<br />start stacking.</h2></div>
          <div className="faq-list">
            {FAQS.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary><span>{faq.question}</span><i>+</i></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="final-cta">
          <div className="final-grid" aria-hidden="true" />
          <div className="final-badge"><Sparkles aria-hidden="true" /> Your next launch starts here</div>
          <h2>Make your body of work<br />impossible to overlook.</h2>
          <p>Claim your corner of the internet. Build free, publish when it feels right.</p>
          <UsernameClaim compact />
        </section>
      </main>

      <footer className="site-footer">
        <div className="footer-top"><BrandLogo /><p>For people who would rather ship than self-promote.</p></div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} MakerStack</span>
          <div><Link to="/pricing">Pricing</Link><Link to="/terms">Terms</Link><Link to="/privacy">Privacy</Link><a href={appUrl('/login')}>Log in</a></div>
        </div>
      </footer>
    </div>
  )
}
